import { ComponentType, FC } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ConnectedComponent } from 'react-redux';
import generateAsyncRouteComponent from '../components/AsyncComponent';

interface DefaultModule<T> {
  default: T;
}
const getDefaultExport = <T>(module: DefaultModule<T>): T => module.default;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncRouteComponent = ComponentType<any> & {
  load: () => Promise<void>;
};

export interface Route {
  path: string;
  Component: AsyncRouteComponent;
  exact?: boolean;
}

export enum InstrumentKind {
  bass = 'bass',
  guitar = 'guitar',
  ukulele = 'ukulele',
}

export function isInstrumentKind(instStr?: string | null): instStr is InstrumentKind {
  return !!instStr && instStr in InstrumentKind;
}

export interface Instrument {
  instrument: InstrumentKind;
}

export interface BrowseSkillsParams extends Instrument {
  skillType: string;
  offset: string;
}

export interface BrowseSongsParams extends Instrument {
  type: string;
  genre: string;
}

interface InjectedIntlFake {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intl: any;
}

export type InstrumentSkillsProps = RouteComponentProps<Instrument> & InjectedIntlFake;
export type InstrumentSongsProps = InstrumentSkillsProps;

export type BrowseSkillsRouteProps = RouteComponentProps<BrowseSkillsParams>;
export type BrowseSongsRouteProps = RouteComponentProps<BrowseSongsParams>;

export interface LessonsParams {
  lessonSlug: string;
}
export interface PagesParams {
  slug: string;
}
export interface PlansParams {
  plan: string;
}

export interface PracticeParams {
  practiceSlug: string;
}

export type LessonsRouteProps = RouteComponentProps<LessonsParams>;
export type PagesRouteProps = RouteComponentProps<PagesParams>;
export type PlansRouteProps = RouteComponentProps<PlansParams>;
export type PracticeRouteProps = RouteComponentProps<PracticeParams>;
// Lessons route is wrapped in connect
type LessonsRouteComponent = ConnectedComponent<
  ComponentType<LessonsRouteProps>,
  LessonsRouteProps
>;
type PagesRouteComponent = ConnectedComponent<ComponentType<PagesRouteProps>, PagesRouteProps>;
type PlansRouteComponent = ConnectedComponent<ComponentType<PlansRouteProps>, PlansRouteProps>;
type PracticeRouteComponent = ConnectedComponent<
  ComponentType<PracticeRouteProps>,
  PracticeRouteProps
>;

export interface PathParams {
  level: string;
}

type PathRouteParams = RouteComponentProps<PathParams>;
type PathRouteComponent = ConnectedComponent<ComponentType<PathRouteParams>, PathRouteParams>;

// The top-level routes
const routes: Route[] = [
  {
    path: '/',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "HomeRoute" */ './home/components/HomeContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/songs',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "SongsRoute" */ './songs/components/SongsContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/admin/video-preview/:videoId',
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "AdminRoute" */ './admin/components/VideoPreviewContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/articles',
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "ArticlesRoute" */ './articles/components/ArticlesFeed').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/lesson/:lessonSlug',
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<any>> =>
        import(/* webpackChunkName: "LessonRoute" */ './lesson/components/LessonContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/course/:courseSlug',
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<any>> =>
        import(/* webpackChunkName: "CourseRoute" */ './course/components/CourseContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/courses/:courseSlug',
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "CoursesRoute" */ './courses/course/components/CourseContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/favorites',
    exact: true,
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "InstrumentSkillsRoute" */ './favorites/FavoritesContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/skills',
    exact: true,
    Component: generateAsyncRouteComponent<InstrumentSkillsProps, FC<InstrumentSkillsProps>>({
      loader: (): Promise<FC<InstrumentSkillsProps>> =>
        import(/* webpackChunkName: "InstrumentSkillsRoute" */ './instrument/Skills').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/songs',
    exact: true,
    Component: generateAsyncRouteComponent<InstrumentSongsProps, FC<InstrumentSongsProps>>({
      loader: (): Promise<FC<InstrumentSongsProps>> =>
        import(/* webpackChunkName: "InstrumentSongsRoute" */ './instrument/Songs').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/skills/:skillType?/:offset?',
    Component: generateAsyncRouteComponent<BrowseSkillsRouteProps, FC<BrowseSkillsRouteProps>>({
      loader: (): Promise<FC<BrowseSkillsRouteProps>> =>
        import(
          /* webpackChunkName: "instrumentSkillsTypeRoute" */ './instrument/BrowseSkills'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/artists/:type?/:genre?',
    Component: generateAsyncRouteComponent<BrowseSongsRouteProps, FC<BrowseSongsRouteProps>>({
      loader: (): Promise<FC<BrowseSongsRouteProps>> =>
        import(
          /* webpackChunkName: "instrumentArtistsTypeRoute" */ './instrument/BrowseArtists'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/:type/:genre/:offset?',
    Component: generateAsyncRouteComponent<BrowseSongsRouteProps, FC<BrowseSongsRouteProps>>({
      loader: (): Promise<FC<BrowseSongsRouteProps>> =>
        import(
          /* webpackChunkName: "instrumentArtistsTypeRoute" */ './instrument/BrowseSongs'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress:type?',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "myProgressRoute" */ './my-progress/components/MyProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress/courses',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "myProgressCoursesRoute" */ './my-progress/components/CourseProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress/:type',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "myProgressLessonsRoute" */ './my-progress/components/LessonsProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/lessons/:lessonSlug',
    Component: generateAsyncRouteComponent<LessonsRouteProps, LessonsRouteComponent>({
      loader: (): Promise<LessonsRouteComponent> =>
        import(
          /* webpackChunkName: "LessonsRoute" */ './lessons/lesson/components/LessonContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/pages/:slug',
    exact: true,
    Component: generateAsyncRouteComponent<PagesRouteProps, PagesRouteComponent>({
      loader: (): Promise<PagesRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(/* webpackChunkName: "pagesRoute" */ './pages/components/PagesContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/path/:level?',
    exact: true,
    Component: generateAsyncRouteComponent<PathRouteParams, PathRouteComponent>({
      loader: (): Promise<PathRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore TODO SubscriptionCheck needs work
        import(/* webpackChunkName: "pathRoute" */ './path/components/PathContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/paths/:path?',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "pathsRoute" */ './paths/path/components/PathContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/plans/:plan?',
    exact: true,
    Component: generateAsyncRouteComponent<PlansRouteProps, PlansRouteComponent>({
      loader: (): Promise<PlansRouteComponent> =>
        import('./plans/components/PlansContainer').then(getDefaultExport),
    }),
  },
  {
    path: '/checkout',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(
          /* webpackChunkName: "checkoutRoute" */ './checkout/components/CheckoutContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/quiz',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "quizRoute" */ './quiz/components/QuizContainer').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/restricted',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "restrictedRoute" */ './restricted/components/Restricted').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/redeem',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "redeemRoute" */ './redeem/components/RedeemContainer').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/redeem/success',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "redeemRoute" */ './redeem/components/RedeemSuccess').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/redeem/error',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "redeemRoute" */ './redeem/components/RedeemError').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/search',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "searchRoute" */ './search/search-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/search/results',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "searchResultsRoute" */ './search/results-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/tab-viewer',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "tabViewerRoute" */ './tab-viewer/tab-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/practice/:practiceSlug',
    exact: true,
    Component: generateAsyncRouteComponent<PracticeRouteProps, PracticeRouteComponent>({
      loader: (): Promise<PracticeRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(
          /* webpackChunkName: "practiceRoute" */ './practice/components/PracticeSheetContainer'
        ).then(getDefaultExport),
    }),
  },
];
export default routes;
